/**
 * MOBY Shared Cart State
 * - Shared /cart.js reads (cache + in-flight dedupe).
 * - The only cart-write owner: every write (paid add, change, update, gift form
 *   POST) runs one at a time through exclusive(). Gifts, plan guards and the
 *   first-order code are applied by one coalesced settle job (requestSettle),
 *   whose steps the cart drawer registers via registerSettleSteps().
 * - A write whose response is lost is quarantined until a known response or a
 *   cart read that positively shows its effect. Time never releases it.
 * Usage: const cart = await window.MobyCart.get();
 */
(function() {
  var cache = null;
  var inflight = null;
  var TTL = 5000; // 5s cache
  var lastFetch = 0;
  // Bumped by every invalidate(). A fetch that started before an invalidate
  // must not write its (pre-change) snapshot into the cache when it lands
  // late — a gift add that took a second or two makes that overlap common,
  // and a stale snapshot would make the reconciler re-add the gift.
  var generation = 0;
  var READ_TIMEOUT = 10000;
  var SLOW_WRITE = 8000;
  var QUARANTINE_KEY = 'moby-cart-unresolved-write';

  function root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  function emit(name, detail) {
    try {
      if (typeof document.dispatchEvent === 'function') document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (e) {}
  }

  function timedFetch(url, options) {
    // Reads may time out; writes never do (see rawPost).
    if (typeof AbortController === 'undefined' || typeof setTimeout === 'undefined') return fetch(url, options);
    var controller = new AbortController();
    var timer = setTimeout(function() { controller.abort(); }, READ_TIMEOUT);
    return fetch(url, Object.assign({}, options, { signal: controller.signal }))
      .finally(function() { clearTimeout(timer); });
  }

  function fetchCart() {
    if (inflight) return inflight;

    var now = Date.now();
    if (cache && (now - lastFetch) < TTL) {
      return Promise.resolve(cache);
    }

    var myGeneration = generation;
    var request = timedFetch(root() + 'cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (myGeneration === generation) {
        cache = data;
        lastFetch = Date.now();
        inflight = null;
      }
      return data;
    })
    .catch(function(err) {
      if (myGeneration === generation) inflight = null;
      throw err;
    });

    inflight = request;
    return request;
  }

  function invalidate() {
    cache = null;
    lastFetch = 0;
    inflight = null;
    generation += 1;
  }

  function freshCart() {
    invalidate();
    return fetchCart();
  }

  // ---------------------------------------------------------------------------
  // Coordinator state
  // ---------------------------------------------------------------------------
  var queue = Promise.resolve();
  var jobs = 0;               // queued + running jobs
  var writeCount = 0;         // bumped by every write attempt
  var writesInFlight = 0;     // writes sent but not yet answered or resolved
  var settledWriteCount = -1;
  var slowWrites = 0;
  var quarantine = null;      // { effect, startedAt, lastRead, checkoutAllowed, resolvers }
  var settleQueued = null;    // promise of a queued, not yet started settle job
  var settling = false;
  var settledGeneration = 0;
  var lastSettled = null;     // { cart, generation, status, error }
  var settleSteps = null;
  var stepsRegistered = null;
  var stepsReady = new Promise(function(resolve) { stepsRegistered = resolve; });
  var intents = [];           // per-session paid intents for theme add_to_cart
  var lastSections = null;    // drawer HTML rendered by the latest offer write

  function snapshot() {
    return {
      busy: jobs > 0, quarantined: Boolean(quarantine), slow: slowWrites > 0, settling: settling || Boolean(settleQueued), purchasePending: purchasePending,
      settledGeneration: settledGeneration, writeCount: writeCount, writesInFlight: writesInFlight, unsettledWrites: writeCount !== settledWriteCount,
      status: lastSettled ? lastSettled.status : null,
      quarantineCheckoutAllowed: Boolean(quarantine && quarantine.checkoutAllowed),
      lastRead: quarantine ? quarantine.lastRead : null
    };
  }
  function emitState() { emit('moby:cart-state', snapshot()); }

  function purchaseConfig() {
    var drawer = document.getElementById && document.getElementById('moby-cart-drawer');
    return drawer ? drawer.dataset : {};
  }

  // FIFO mutex for cart writes. A job receives the unlocked primitives; they only
  // work while that job runs. New jobs are refused while a write is unresolved.
  function exclusive(fn) {
    if (quarantine) {
      var refused = new Error(purchaseConfig().textQuarantine || 'We’re still confirming your last cart update.');
      refused.mobyQuarantined = true;
      return Promise.reject(refused);
    }
    jobs += 1;
    emitState();
    var run = queue.then(function() {
      var job = { active: true };
      var ctx = jobContext(job);
      return Promise.resolve().then(function() { return fn(ctx); }).finally(function() {
        job.active = false;
        jobs -= 1;
        emitState();
      });
    });
    queue = run.catch(function() {});
    return run;
  }

  function jobContext(job) {
    var guard = function(name, impl) {
      return function() {
        if (!job.active) throw new Error('MobyCart ' + name + ' used outside its job');
        return impl.apply(null, arguments);
      };
    };
    return {
      read: guard('read', freshCart),
      post: guard('post', rawPost),
      addGift: guard('addGift', submitGiftForm),
      applyOffer: guard('applyOffer', applyOffer),
      dryRun: false
    };
  }

  // ---------------------------------------------------------------------------
  // Unknown-outcome quarantine (§2a): released only by a known response or a
  // read that shows the expected effect. Never by time, unchanged reads or reload.
  // ---------------------------------------------------------------------------
  var propsKey = function(value) { return JSON.stringify(Object.entries(value || {}).filter(function(entry) { return entry[0] !== '__proto__'; }).sort()); };

  function planId(item) {
    return String(item.selling_plan || (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || '');
  }

  function effectHolds(effect, cart) {
    var items = (cart && cart.items) || [];
    if (effect.type === 'line') {
      var line = items.find(function(l) {
        return String(l.variant_id) === String(effect.variant) && planId(l) === String(effect.plan || '') && propsKey(l.properties) === effect.props;
      });
      var qty = line ? line.quantity : 0;
      if (effect.min != null) return qty >= effect.min;
      return qty === effect.eq;
    }
    if (effect.type === 'key-absent') return !items.some(function(l) { return l.key === effect.key; });
    if (effect.type === 'key-qty') return items.some(function(l) { return l.key === effect.key && l.quantity === effect.eq; });
    if (effect.type === 'gift') return items.some(function(l) { return String(l.variant_id) === String(effect.variant); });
    if (effect.type === 'gifts') return effect.variants.every(function(id) { return items.some(function(l) { return String(l.variant_id) === id && !planId(l) && l.properties && l.properties._welcome_gift === '1' && l.quantity >= 1 && l.final_line_price === 0; }); });
    // Shopify removes an attribute set to '', so absent equals ''.
    if (effect.type === 'attr') return (((cart && cart.attributes) || {})[effect.name] || '') === (effect.value || '');
    // Any successful read is the evidence (used to look at a cart after a
    // known rejection when the first read failed).
    if (effect.type === 'any-read') return true;
    if (effect.type === 'variant-min') {
      return items.filter(function(l) { return String(l.variant_id) === String(effect.variant); })
        .reduce(function(sum, l) { return sum + l.quantity; }, 0) >= effect.min;
    }
    if (effect.type === 'updates') {
      return Object.keys(effect.updates).every(function(key) {
        var target = items.find(function(l) { return l.key === key; });
        return effect.updates[key] === 0 ? !target : Boolean(target) && target.quantity === effect.updates[key];
      });
    }
    return false;
  }

  // The write in flight (or unresolved) is stored from the moment it is sent,
  // so a reload while its response is still pending — not only after a
  // network error — restores evidence-only protection in the next page.
  var pendingWrite = null;    // { effect, startedAt, intent }
  // A known partial 422 whose retained quantity is not yet resolved. Saved
  // before its first recovery read, so a reload during that read resumes it.
  var pendingRecovery = null; // { effect: any-read, startedAt, recover }
  function persistQuarantine() {
    try {
      var record = quarantine || pendingWrite || pendingRecovery;
      if (record) {
        sessionStorage.setItem(QUARANTINE_KEY, JSON.stringify({
          effect: record.effect, startedAt: record.startedAt, intent: record.intent || null,
          retry: record.retry || null, retryState: record.retryState || null, recover: record.recover || null
        }));
      } else sessionStorage.removeItem(QUARANTINE_KEY);
    } catch (e) {}
  }

  function beginWrite(effect, intent) {
    writeCount += 1;
    writesInFlight += 1;
    pendingWrite = { effect: effect || { type: 'unknown' }, startedAt: Date.now(), intent: intent || null };
    persistQuarantine();
    return pendingWrite;
  }

  function endWrite() {
    writesInFlight = Math.max(0, writesInFlight - 1);
    pendingWrite = null;
    if (!quarantine) persistQuarantine();
  }

  // Returns a promise that resolves (with the cart that proved the effect, or with
  // `known` when a late known response arrives) only once the write is resolved.
  // extra.retry ({ endpoint, body }) is an absolute write whose last attempt got
  // a known rejection (a partial-422 rollback). extra.retryState is persisted:
  // 'ready' allows one re-send; it becomes 'sent' (saved BEFORE the request
  // goes out) and only a known rejection makes it 'ready' again. A 'sent'
  // re-send that never answered — in this page or before a reload — is never
  // repeated; only reads decide.
  // extra.recover ({ item, original }) is a known partial-422 whose retained
  // quantity is not yet known: the first successful read resolves it, and if
  // units were retained the hold becomes their rollback instead of releasing.
  function enterQuarantine(effect, startedAt, intent, restored, extra) {
    extra = extra || {};
    return new Promise(function(resolve) {
      if (quarantine) { quarantine.resolvers.push(resolve); return; }
      quarantine = {
        effect: effect, startedAt: startedAt || Date.now(), intent: intent || null, restored: Boolean(restored),
        retry: extra.retry || null, retryState: extra.retry ? (extra.retryState || 'ready') : null, recover: extra.recover || null,
        reading: null, lastRead: null, checkoutAllowed: false, resolvers: [resolve], timer: null, step: 0
      };
      persistQuarantine();
      emitState();
      recoveryRead();
    });
  }

  function leaveQuarantine(result) {
    if (!quarantine) return;
    var resolvers = quarantine.resolvers;
    if (quarantine.timer) clearTimeout(quarantine.timer);
    // A paid add restored after a reload that is now proven committed is still
    // tracked once (the page that sent it is gone).
    if (quarantine.restored && quarantine.intent && result && result.cart) intents.push({ item: quarantine.intent, tracked: false });
    quarantine = null;
    pendingWrite = null;
    writesInFlight = 0;
    persistQuarantine();
    invalidate();
    emitState();
    resolvers.forEach(function(resolve) { resolve(result); });
  }

  var RECOVERY_DELAYS = [1000, 2000, 4000, 8000];
  function scheduleRecovery() {
    if (!quarantine || typeof setTimeout === 'undefined') return;
    if (quarantine.timer) clearTimeout(quarantine.timer);
    var delay = quarantine.step < RECOVERY_DELAYS.length ? RECOVERY_DELAYS[quarantine.step] : 30000;
    quarantine.step += 1;
    quarantine.timer = setTimeout(function() {
      if (typeof document.visibilityState === 'string' && document.visibilityState === 'hidden' && quarantine && quarantine.step > RECOVERY_DELAYS.length) {
        scheduleRecovery();
        return;
      }
      recoveryRead();
    }, delay);
  }

  // Recovery reads are coalesced: Check again, visibility and timers share one
  // read, and the held re-send below is the only write a recovery can start.
  function recoveryRead() {
    if (!quarantine) return Promise.resolve();
    var current = quarantine;
    if (current.reading) return current.reading;
    current.reading = freshCart().then(function(cart) {
      current.reading = null;
      if (quarantine !== current) return;
      current.lastRead = cart;
      if (effectHolds(current.effect, cart)) {
        if (!current.recover) { leaveQuarantine({ cart: cart }); return; }
        // Known partial 422: now that the cart is readable, roll back retained units first.
        var line = (cart.items || []).find(function(l) { return sameLine(l, current.recover.item); });
        if (!line || line.quantity <= current.recover.original) { leaveQuarantine({ cart: cart }); return; }
        current.effect = lineEffect(current.recover.item, current.recover.original, true);
        current.retry = { endpoint: 'change', body: { id: line.key, quantity: current.recover.original } };
        current.retryState = 'ready';
        current.recover = null;
        persistQuarantine();
      }
      if (current.retry && current.retryState === 'ready' && writesInFlight === 0) return retryHeldWrite(current);
      return dryRunInvariants(cart).then(function(allowed) {
        if (quarantine !== current) return;
        current.checkoutAllowed = allowed;
        emitState();
        scheduleRecovery();
      });
    }, function() {
      current.reading = null;
      // A failed read is not evidence of anything. Keep waiting.
      if (quarantine === current) scheduleRecovery();
    });
    return current.reading;
  }

  function retryHeldWrite(current) {
    // Saved before sending, so neither this page nor a reloaded one re-sends
    // while this request's outcome is unknown.
    current.retryState = 'sent';
    persistQuarantine();
    writeCount += 1;
    writesInFlight += 1;
    return fetch(root() + 'cart/' + current.retry.endpoint + '.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(current.retry.body)
    }).then(function(response) {
      writesInFlight = Math.max(0, writesInFlight - 1);
      invalidate();
      if (quarantine !== current) return;
      // Known answer: a rejection may be re-sent later; an accepted one is
      // confirmed by the next read. Either way the next read is the evidence.
      current.retryState = response.ok ? 'done' : 'ready';
      persistQuarantine();
      // Accepted: verify now. Rejected: wait the recovery backoff before trying again.
      if (response.ok) recoveryRead(); else scheduleRecovery();
    }, function() {
      writesInFlight = Math.max(0, writesInFlight - 1);
      // Unknown outcome: stays 'sent' (persisted); never re-sent.
      if (quarantine === current) scheduleRecovery();
    });
  }

  // Checkout may proceed during quarantine only if the latest read already needs
  // no write from any settle step (offer code, gifts and plans all in place).
  function dryRunInvariants(cart) {
    if (!settleSteps) return Promise.resolve(false);
    var needsWrite = new Error('needs write');
    var fail = function() { throw needsWrite; };
    var ctx = {
      read: function() { return Promise.resolve(cart); },
      post: fail, addGift: fail,
      applyOffer: function(c) { return offerDecision(c, purchaseConfig()).then(function(decision) { if (decision.write) throw needsWrite; return { cart: c, changed: false }; }); },
      dryRun: true
    };
    return Promise.resolve().then(function() { return settleSteps(ctx, cart); })
      .then(function(result) { return Boolean(result) && result.status === 'verified'; }, function() { return false; });
  }

  // Writes: no client timeout and no abort. A network error leaves the outcome
  // unknown, so the caller waits in quarantine for evidence.
  // `recover` (paid adds only): on a known rejection the saved record switches
  // to the partial-422 recovery as soon as the status is known, before the
  // body is read, so no reload can land between the write record and it.
  function rawPost(endpoint, body, effect, intent, recover) {
    var write = beginWrite(effect, intent);
    var startedAt = write.startedAt;
    var slow = false;
    var slowTimer = typeof setTimeout !== 'undefined' ? setTimeout(function() { slow = true; slowWrites += 1; emitState(); }, SLOW_WRITE) : null;
    var finish = function() {
      if (slowTimer) clearTimeout(slowTimer);
      if (slow) { slow = false; slowWrites -= 1; emitState(); }
    };
    return fetch(root() + 'cart/' + endpoint + '.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function(response) {
      finish();
      if (!response.ok) {
        if (recover) pendingRecovery = { effect: { type: 'any-read' }, startedAt: Date.now(), recover: recover };
        endWrite();
      }
      // Accepted: the write record stays until the body is parsed; a reload in
      // between restores the write and resolves it on its observed effect.
      return response.json().catch(function() { return {}; }).then(function(data) {
        if (response.ok) endWrite();
        invalidate();
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'Could not update cart');
          error.mobyRejected = true;
          error.status = response.status;
          throw error;
        }
        return data;
      });
    }, function() {
      finish();
      return enterQuarantine(write.effect, startedAt, write.intent).then(function(result) {
        if (result && result.cart) return result.cart;
        return freshCart();
      });
    });
  }

  // Add ONE Welcome Kit gift line without emitting Shopify's
  // `product_added_to_cart` web-pixel event.
  //
  // WHY THIS IS NOT A fetch() CALL — do not "simplify" it back:
  // Shopify's web-pixel bridge observes the page's own fetch/XHR traffic and
  // publishes one `product_added_to_cart` per line it sees POSTed to the
  // /cart/add(.js) route. Because the Welcome Kit adds two gift lines right
  // after the paid line, Meta/GA4/Google Ads recorded THREE AddToCart events
  // for a single subscription add-to-cart click. A plain HTML form POST
  // submitted from a hidden same-origin iframe is a document navigation in a
  // different document — the bridge never sees it, so no event is published.
  //
  // Verified live on drinkmoby.com on 2026-09-19: paid line via /cart/add.js
  // produced exactly 1 Meta AddToCart, both gifts via this path produced 0
  // extra events, and all three lines were present in /cart.js and rendered
  // in checkout at 0 kr.
  //
  // `return_to` points at cart.js so the iframe lands on a JSON document
  // rather than a themed page — otherwise a PageView would fire instead.
  //
  // There is no JSON response to inspect here. Callers must treat "line is
  // present in a fresh /cart.js read after this resolves" as the success
  // signal. The promise resolves when the iframe loads (Shopify answered). If
  // it has not loaded after 8s the outcome is unknown: the iframe is kept (so
  // the POST is not cancelled) and the write is quarantined until the frame
  // loads or a read shows the gift line.
  function submitGiftForm(variantId) {
    var ids = Array.isArray(variantId) ? Array.from(new Set(variantId.map(String))) : [String(variantId)];
    if (!ids.length) return Promise.resolve(null);
    var write = ids.length === 1
      ? beginWrite({ type: 'gift', variant: String(variantId) })
      : beginWrite({ type: 'gifts', variants: ids });
    return new Promise(function(resolve) {
      var frame = document.createElement('iframe');
      frame.style.display = 'none';
      frame.setAttribute('aria-hidden', 'true');
      document.body.appendChild(frame);

      var settled = false;
      var done = function(cart) {
        if (settled) return;
        settled = true;
        try { frame.remove(); } catch (e) {}
        if (!quarantine) endWrite();
        invalidate();
        if (quarantine && JSON.stringify(quarantine.effect) === JSON.stringify(write.effect)) leaveQuarantine({ known: true });
        resolve(cart || null);
      };

      var doc = frame.contentDocument;
      if (!doc) { done(); return; }

      var form = doc.createElement('form');
      form.method = 'POST';
      form.action = root() + 'cart/add';
      // layout/theme.liquid registers a global /cart/add submit interceptor on
      // the TOP-LEVEL document, which never receives this iframe document's
      // submit event. The marker keeps the form exempt anyway, in case that
      // listener is ever moved somewhere that could observe it.
      form.setAttribute('data-custom-handler', 'true');

      var addField = function(name, value) {
        var input = doc.createElement('input');
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };
      if (ids.length === 1) {
        addField('id', variantId);
        addField('quantity', '1');
        addField('properties[_welcome_gift]', '1');
      } else {
        ids.forEach(function(id, index) {
          addField('items[' + index + '][id]', id);
          addField('items[' + index + '][quantity]', '1');
          addField('items[' + index + '][properties][_welcome_gift]', '1');
        });
      }
      addField('return_to', root() + 'cart.js');
      doc.body.appendChild(form);

      // Firefox fires the initial about:blank `load` asynchronously, i.e.
      // possibly AFTER this listener is attached and the form is submitted.
      // Settling on that event would remove the iframe mid-POST and lose the
      // gift, so a readable about:blank is ignored. Anything else settles:
      // cart.js on success, or — for a gift that cannot be added — Shopify's
      // 422, which refuses to be framed (verified 2026-09-19: the browser
      // blocks it, so no page and no pixel bootstrap ever render) and whose
      // URL is therefore unreadable from here.
      // On success the frame shows the /cart.js JSON (same origin): use it as
      // the post-add cart and save a read. Anything unreadable resolves null.
      frame.addEventListener('load', function() {
        var href = '';
        try { href = frame.contentWindow.location.href; } catch (e) { href = '(blocked)'; }
        if (href === 'about:blank') return;
        var cart = null;
        try {
          var parsed = JSON.parse(frame.contentDocument.body.textContent);
          if (parsed && Array.isArray(parsed.items)) cart = parsed;
        } catch (e) {}
        done(cart);
      });
      setTimeout(function() {
        if (settled) return;
        enterQuarantine(write.effect, write.startedAt).then(function(result) { done(result && result.cart); });
      }, SLOW_WRITE);
      form.submit();
    });
  }

  // Public gift helper (outside settle jobs). Inside a settle job, use ctx.addGift.
  // No purchase surface calls the public gift/offer helpers; a call while a
  // settle job runs can only be a settle step that should use ctx (it would
  // otherwise queue behind itself forever), so fail fast.
  function refuseInsideSettle() {
    if (settling) throw new Error('MobyCart reentrant write: use the settle job context');
  }

  function addGiftLine(variantId) {
    refuseInsideSettle();
    return exclusive(function(ctx) { return ctx.addGift(variantId); });
  }

  // ---------------------------------------------------------------------------
  // First-order code. Shopify validates new-customer eligibility and the
  // first-payment limit. This only selects the configured code; it never
  // changes subscription contracts.
  // ---------------------------------------------------------------------------
  function offerDecision(cart, config) {
    config = config || {};
    var monthly = String(config.plan1boxId || '');
    var quarterly = String(config.plan3boxId || '');
    var singleBoxPlans = new Set((config.singleBoxPlanIds || monthly).split(',').map(function(id) { return id.trim(); }).filter(Boolean));
    var wanted = '';
    var hasMonthly = false;
    var hasQuarterly = false;
    (cart.items || []).forEach(function(item) {
      if (String(item.product_id) !== String(config.eligibleProductId)) return;
      var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      if (!plan) return;
      if (String(plan.id) === quarterly && item.quantity >= 3) hasQuarterly = true;
      if (singleBoxPlans.has(String(plan.id))) hasMonthly = true;
    });
    // The two first-payment codes apply different amounts per box. Shopify
    // basic discounts cannot target selling-plan IDs within the same product.
    // Ask for one schedule per order instead of silently mispricing a mixed cart.
    if (hasMonthly && hasQuarterly && config.offerEligible !== 'false') {
      var conflict = new Error(config.textOfferScheduleConflict || 'Choose one subscription schedule per order. Remove one of the subscription options to continue.');
      conflict.mobyOfferSchedule = true;
      return Promise.reject(conflict);
    }
    if (config.offerEligible !== 'false') {
      wanted = hasQuarterly ? (config.firstOrderCode3month || '') : hasMonthly ? (config.firstOrderCode1month || '') : '';
    }
    var previous = (cart.attributes || {})._moby_offer_code || '';
    if (wanted === previous) return Promise.resolve({ write: false, wanted: wanted });
    // Preserve other visible customer codes. Remove only our managed offer codes.
    var otherCodes = new Set();
    (cart.cart_level_discount_applications || []).concat(
      (cart.items || []).flatMap(function(item) { return item.line_level_discount_allocations || []; }).map(function(allocation) { return allocation.discount_application; })
    ).filter(Boolean).forEach(function(discount) {
      if (discount.type === 'discount_code' && discount.title !== previous && discount.title !== config.firstOrderCode1month && discount.title !== config.firstOrderCode3month) otherCodes.add(discount.title);
    });
    if (wanted) otherCodes.add(wanted);
    return Promise.resolve({ write: true, wanted: wanted, body: { discount: Array.from(otherCodes).join(','), attributes: { _moby_offer_code: wanted } } });
  }

  // Unlocked: only call from inside a job.
  function applyOffer(cart, config) {
    return offerDecision(cart, config || purchaseConfig()).then(function(decision) {
      if (!decision.write) return { cart: cart, changed: false };
      var body = decision.body;
      var hasDrawer = document.getElementById && document.getElementById('moby-cart-drawer');
      if (hasDrawer) body = Object.assign({}, body, { sections: 'moby-cart-drawer' });
      return rawPost('update', body, { type: 'attr', name: '_moby_offer_code', value: decision.wanted })
        .then(function(updated) {
          var sections = null;
          if (updated && updated.sections) {
            sections = updated.sections['moby-cart-drawer'];
            // Reuse only while no later write has changed the cart.
            lastSections = { html: updated.sections['moby-cart-drawer'], writeCount: writeCount };
            updated = Object.assign({}, updated);
            delete updated.sections;
          }
          return { cart: updated, changed: true, sections: sections, writeCount: writeCount };
        }, function(error) {
          if (error && error.mobyRejected) {
            var failure = new Error('Could not apply the first-order offer');
            failure.mobyOfferFailed = true;
            throw failure;
          }
          throw error;
        });
    });
  }

  function reconcileOfferDiscount(cart, config) {
    try { refuseInsideSettle(); } catch (error) { return Promise.reject(error); }
    return exclusive(function() { return applyOffer(cart, config); });
  }

  // Native quick-add (assets/product-form.js) posts its own FormData. It joins
  // the queue, and a lost response is quarantined like any other write; once a
  // read proves the add, the caller gets a plain success response.
  function nativeAdd(post, variantId, quantity) {
    return exclusive(function(ctx) {
      return ctx.read().then(function(before) {
        var original = (before.items || []).filter(function(l) { return String(l.variant_id) === String(variantId); })
          .reduce(function(sum, l) { return sum + l.quantity; }, 0);
        var write = beginWrite({ type: 'variant-min', variant: String(variantId), min: original + (Number(quantity) || 1) });
        return post().then(function(response) {
          endWrite();
          invalidate();
          return response;
        }, function() {
          return enterQuarantine(write.effect, write.startedAt).then(function() {
            return { ok: true, status: 200, json: function() { return Promise.resolve({}); } };
          });
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Settle: plan guards → offer → gifts, in one coalesced job.
  // ---------------------------------------------------------------------------
  var OK_STATUSES = ['verified', 'gift-warning'];

  function defaultSteps(ctx, cart) {
    return ctx.applyOffer(cart).then(function(offer) { return { status: 'verified', cart: offer.cart }; });
  }

  function registerSettleSteps(fn) {
    settleSteps = fn;
    stepsRegistered();
  }

  function requestSettle(options) {
    var force = Boolean(options && options.force);
    if (quarantine) return Promise.resolve(lastSettled);
    if (settleQueued) return settleQueued;
    if (!force && !settling && lastSettled && writeCount === settledWriteCount) return Promise.resolve(lastSettled);
    var job = exclusive(function(ctx) {
      settleQueued = null;
      settling = true;
      emitState();
      return runSettle(ctx).finally(function() { settling = false; emitState(); });
    });
    settleQueued = job;
    emitState();
    job.catch(function() {});
    return job;
  }

  // The drawer script is deferred, so it has registered its steps by
  // DOMContentLoaded. Before that (or on pages without a drawer) only the
  // offer step runs.
  function stepsAvailable() {
    if (settleSteps || typeof document.readyState !== 'string' || document.readyState !== 'loading') return Promise.resolve();
    return new Promise(function(resolve) {
      document.addEventListener('DOMContentLoaded', function() { resolve(); }, { once: true });
      stepsReady.then(resolve);
    });
  }

  function runSettle(ctx) {
    var status = 'verified';
    var error = null;
    var read = function() {
      return ctx.read().catch(function(failure) { failure.mobyReadFailed = true; throw failure; });
    };
    var latest = null;
    return stepsAvailable().then(read).then(function(cart) {
      var steps = settleSteps || defaultSteps;
      return Promise.resolve(steps(ctx, cart)).then(function(result) {
        status = (result && result.status) || 'verified';
        error = (result && result.error) || null;
        latest = (result && result.cart) || null;
      });
    }).then(function() {
      // Steps hand back the cart from their last write response or read; the
      // queue guarantees nothing wrote after it, so a final read is redundant.
      return latest && Array.isArray(latest.items) ? latest : read();
    }).then(function(finalCart) {
      return publish(finalCart, status, error);
    }, function(failure) {
      if (failure && failure.mobyReadFailed) return publish(null, 'read-failed', failure);
      return read().then(function(cart) { return publish(cart, 'unverified-pricing', failure); }, function(readFailure) {
        return publish(null, 'read-failed', readFailure);
      });
    });
  }

  function publish(cart, status, error) {
    settledWriteCount = writeCount;
    settledGeneration += 1;
    var sections = lastSections && lastSections.writeCount === writeCount ? lastSections.html : null;
    lastSections = null;
    lastSettled = { cart: cart || (lastSettled && lastSettled.cart) || null, generation: settledGeneration, writeCount: writeCount, status: status, error: error, fresh: Boolean(cart), sections: sections };
    if (cart) {
      // Seed the shared read cache so settled listeners do not refetch.
      cache = cart;
      lastFetch = Date.now();
      inflight = null;
    }
    emit('moby:cart-settled', lastSettled);
    if (cart) {
      trackIntents(cart);
      emit('cart:updated', Object.assign({}, cart, { data: { source: 'moby-settled' } }));
      if (document.querySelector && document.querySelector('cart-items-component')) {
        emit('cart:update', { resource: cart, sourceId: 'moby-settled', data: { source: 'moby-settled', itemCount: cart.item_count } });
      }
    }
    emitState();
    return lastSettled;
  }

  // ---------------------------------------------------------------------------
  // Theme add_to_cart (GA4 dataLayer): once per committed paid intent, valued
  // from the settled cart line (discounted final_price), never from add.js.
  // ---------------------------------------------------------------------------
  function trackIntents(cart) {
    intents.forEach(function(intent) {
      if (intent.tracked) return;
      intent.tracked = true;
      var line = (cart.items || []).find(function(l) { return sameLine(l, intent.item); }) ||
        // The plan guard can move the added boxes to another plan line (e.g.
        // 2 + 1 monthly boxes become a quarterly line); value them there.
        (cart.items || []).find(function(l) { return String(l.variant_id) === String(intent.item.id) && l.final_line_price > 0 && Boolean(planId(l)) === Boolean(intent.item.selling_plan); });
      if (!line) return;
      try {
        if (window.MobyTracking && typeof window.MobyTracking.addToCart === 'function') {
          window.MobyTracking.addToCart({ cart: { currency: cart.currency, items: [line] }, variantId: intent.item.id, quantity: intent.item.quantity });
        }
      } catch (e) {}
    });
  }

  // ---------------------------------------------------------------------------
  // Paid adds and subscription replacement
  // ---------------------------------------------------------------------------
  var purchasePending = false;
  var switchNotice = null;

  function planFamily(id, config) {
    if (!id) return '';
    if (id === String(config.plan3boxId)) return 'quarterly';
    return (config.singleBoxPlanIds || config.plan1boxId || '').split(',').map(function(value) { return value.trim(); }).includes(id) ? 'single' : '';
  }

  function replacementLines(cart, item, config) {
    var family = planFamily(planId(item), config);
    if (!family) return [];
    return (cart.items || []).filter(function(line) {
      var otherFamily = planFamily(planId(line), config);
      return String(line.product_id) === String(config.eligibleProductId) && String(line.variant_id) === String(item.id) && otherFamily && otherFamily !== family;
    });
  }

  function purchaseLabel(cart, variantId, sellingPlan, fallback, compact) {
    var config = purchaseConfig();
    var item = { id: variantId, selling_plan: sellingPlan };
    if (!replacementLines(cart, item, config).length) return fallback;
    var quarterly = planFamily(String(sellingPlan), config) === 'quarterly';
    return compact ? (quarterly ? config.textSwitchQuarterlyShort : config.textSwitchMonthlyShort) : (quarterly ? config.textSwitchToQuarterly : config.textSwitchToMonthly);
  }

  function sameLine(line, item) {
    return String(line.variant_id) === String(item.id || item.variant_id) && planId(line) === planId(item) && propsKey(line.properties) === propsKey(item.properties);
  }

  function lineEffect(item, quantity, exact) {
    var effect = { type: 'line', variant: String(item.id || item.variant_id), plan: planId(item), props: propsKey(item.properties) };
    if (exact) effect.eq = quantity; else effect.min = quantity;
    return effect;
  }

  function notifySwitch() {
    emit('moby:subscription-switch');
  }

  function purchaseMutation(action) {
    if (purchasePending) return Promise.reject(new Error(purchaseConfig().textPurchasePending || 'Please wait for the cart to finish updating.'));
    if (quarantine) {
      var refused = new Error(purchaseConfig().textQuarantine || 'We’re still confirming your last cart update.');
      refused.mobyQuarantined = true;
      return Promise.reject(refused);
    }
    purchasePending = true;
    emit('moby:purchase-pending');
    // The settle is queued from inside the job so the queue never looks idle
    // between the paid write and the settle that completes it.
    return exclusive(function(ctx) {
      return Promise.resolve().then(function() { return action(ctx); }).finally(function() { requestSettle({ force: true }); });
    }).finally(function() {
      purchasePending = false;
      emit('moby:purchase-pending');
    });
  }

  // Resolves { outcome: 'committed', items } once Shopify accepted the paid line
  // (or a read proved a lost response applied). Rejects with
  // error.mobyPaidAddCommitted === false when nothing was added.
  function addPaidLine(item, options) {
    var validate = options && options.validate;
    return purchaseMutation(async function(ctx) {
      var config = purchaseConfig();
      var before = await ctx.read();
      if (validate) {
        try { validate(before); }
        catch (validationError) { validationError.mobyPaidAddCommitted = false; validationError.mobyValidation = true; throw validationError; }
      }
      var previous = replacementLines(before, item, config);
      if (previous.length > 1) throw new Error(config.textSwitchFailed || config.textOfferScheduleConflict);
      var existingTarget = before.items.find(function(line) { return sameLine(line, item); });
      var originalQuantity = existingTarget ? existingTarget.quantity : 0;
      var committed = false;
      var previousNotice = switchNotice;
      try {
        // One paid add per click. Never remove the old choice until this succeeds.
        var result;
        var intentItem = { id: item.id, quantity: item.quantity, selling_plan: item.selling_plan, properties: item.properties };
        var recover = { item: { id: item.id, selling_plan: item.selling_plan, properties: item.properties }, original: originalQuantity };
        try { result = await ctx.post('add', { items: [item] }, lineEffect(item, originalQuantity + item.quantity), intentItem, recover); }
        catch (addError) {
          // Shopify answered with a rejection. A stock 422 can still have
          // added part of the quantity, so look before deciding.
          // Until a read proves what was retained, hold every write (no retry
          // may stack on top of retained paid units).
          pendingRecovery = pendingRecovery || { effect: { type: 'any-read' }, startedAt: Date.now(), recover: recover };
          persistQuarantine();
          try {
            var recovered;
            try { recovered = await ctx.read(); }
            catch (readError) {
              recovered = (await enterQuarantine({ type: 'any-read' }, null, null, false, { recover: recover })).cart;
            }
            var recoveredLine = recovered.items.find(function(line) { return sameLine(line, item); });
            if (recoveredLine && recoveredLine.quantity > originalQuantity) {
              var rollback = { id: recoveredLine.key, quantity: originalQuantity };
              var rollbackEffect = lineEffect(item, originalQuantity, true);
              var rolledBack = false;
              for (var attempt = 0; attempt < 2 && !rolledBack; attempt++) {
                try { await ctx.post('change', rollback, rollbackEffect); rolledBack = true; }
                catch (rollbackError) { if (!rollbackError.mobyRejected) throw rollbackError; }
              }
              // Still retained: stay held until a read shows the original quantity.
              if (!rolledBack) await enterQuarantine(rollbackEffect, null, null, false, { retry: { endpoint: 'change', body: rollback } });
            }
          } finally {
            // Retained quantity is resolved (or held by its own quarantine record).
            pendingRecovery = null;
            persistQuarantine();
          }
          throw addError;
        }
        committed = true;
        intents.push({ item: intentItem, tracked: false });
        switchNotice = null;
        notifySwitch();
        if (previous.length) {
          var addedCart = await ctx.read();
          var oldLine = addedCart.items.find(function(line) { return sameLine(line, previous[0]); });
          var removalError = null;
          if (oldLine) {
            try { await ctx.post('change', { id: oldLine.key, quantity: 0 }, { type: 'key-absent', key: oldLine.key }); }
            catch (error) { removalError = error; }
          }
          var switchedCart = await ctx.read();
          if (replacementLines(switchedCart, item, config).length) {
            var addedLine = switchedCart.items.find(function(line) { return sameLine(line, item); });
            if (addedLine) {
              await ctx.post('change', { id: addedLine.key, quantity: originalQuantity }, originalQuantity ? { type: 'key-qty', key: addedLine.key, eq: originalQuantity } : { type: 'key-absent', key: addedLine.key });
              var rolledBack = await ctx.read();
              var remainingTarget = rolledBack.items.find(function(line) { return sameLine(line, item); });
              committed = (remainingTarget ? remainingTarget.quantity : 0) !== originalQuantity;
              if (!committed) intents.pop();
            }
            throw removalError || new Error(config.textSwitchFailed);
          }
          var nextLine = switchedCart.items.find(function(line) { return sameLine(line, item); });
          if (!nextLine) throw new Error(config.textSwitchFailed);
          if (existingTarget && nextLine.quantity !== item.quantity) {
            switchedCart = await ctx.post('change', { id: nextLine.key, quantity: item.quantity }, { type: 'key-qty', key: nextLine.key, eq: item.quantity });
            nextLine = switchedCart.items.find(function(line) { return sameLine(line, item); });
          }
          switchNotice = {
            message: planFamily(planId(item), config) === 'quarterly' ? config.textSwitchedQuarterly : config.textSwitchedMonthly,
            previous: previous[0], current: nextLine, canUndo: !existingTarget
          };
          // Apply the matching code right away so the replaced cart is never
          // left with the previous schedule's code.
          await ctx.applyOffer(switchedCart, config);
          notifySwitch();
        }
        return { outcome: 'committed', items: result && result.items ? result.items : [] };
      } catch (error) {
        error.mobyPaidAddCommitted = committed || Boolean(error.mobyPaidAddCommitted);
        if (!error.mobyPaidAddCommitted) { switchNotice = previousNotice; notifySwitch(); }
        throw error;
      }
    });
  }

  function undoSubscriptionSwitch() {
    return purchaseMutation(async function(ctx) {
      var notice = switchNotice;
      var config = purchaseConfig();
      var cart = await ctx.read();
      var index = notice && notice.canUndo ? cart.items.findIndex(function(line) { return sameLine(line, notice.current) && line.quantity === notice.current.quantity; }) : -1;
      if (index < 0 || replacementLines(cart, { id: notice.current.variant_id, selling_plan: planId(notice.current) }, config).length) {
        switchNotice = null;
        notifySwitch();
        throw new Error(config.textUndoUnavailable || 'The cart has changed. Please choose your subscription again.');
      }
      // Shopify changes a selling plan atomically by line position, not by key.
      // Line-index writes are never retried.
      var restored = await ctx.post('change', {
        line: index + 1, quantity: notice.previous.quantity,
        selling_plan: planId(notice.previous), properties: notice.previous.properties || {}
      }, lineEffect({ id: notice.previous.variant_id, selling_plan: planId(notice.previous), properties: notice.previous.properties || {} }, notice.previous.quantity, true));
      switchNotice = null;
      notifySwitch();
      await ctx.applyOffer(restored, config);
      return ctx.read();
    });
  }

  // Pending-button copy for any purchase surface while its add is in flight.
  function whilePending(render, defaultText) {
    var last = null;
    var update = function() {
      var config = purchaseConfig();
      var state = snapshot();
      var text = state.quarantined ? (config.textCheckingCart || 'Checking your cart…') : state.slow ? (config.textStillUpdating || 'Still updating your cart…') : defaultText;
      if (text === last) return;
      last = text;
      render(text);
    };
    document.addEventListener('moby:cart-state', update);
    update();
    return function() { document.removeEventListener('moby:cart-state', update); };
  }

  // Spinner + text for a button's pending label. Styled by .moby-atc-spinner in base.css.
  function pendingLabel(text) {
    return '<span class="moby-atc-spinner" aria-hidden="true"></span><span class="moby-atc-label">' + text + '</span>';
  }

  window.MobyCart = {
    get: fetchCart,
    invalidate: invalidate,
    exclusive: exclusive,
    requestSettle: requestSettle,
    registerSettleSteps: registerSettleSteps,
    addGiftLine: addGiftLine,
    reconcileOfferDiscount: reconcileOfferDiscount,
    addPaidLine: addPaidLine,
    purchaseLabel: purchaseLabel,
    sameLine: sameLine,
    nativeAdd: nativeAdd,
    lineEffect: lineEffect,
    // A paid add/Undo is in flight or a write is unresolved (not routine settles).
    isMutating: function() { return purchasePending || Boolean(quarantine); },
    isQuarantined: function() { return Boolean(quarantine); },
    state: snapshot,
    lastSettled: function() { return lastSettled; },
    okStatus: function(status) { return OK_STATUSES.indexOf(status) !== -1; },
    checkAgain: function() { return recoveryRead(); },
    whilePending: whilePending,
    pendingLabel: pendingLabel,
    subscriptionSwitch: function() { return switchNotice; },
    undoSubscriptionSwitch: undoSubscriptionSwitch
  };

  // Auto-invalidate on cart events
  document.addEventListener('cart:refresh', invalidate);
  document.addEventListener('cart:updated', function(event) {
    if (!(event && event.detail && event.detail.data && event.detail.data.source === 'moby-settled')) invalidate();
  });

  // An unresolved write survives reloads: keep waiting for evidence.
  try {
    var saved = sessionStorage.getItem(QUARANTINE_KEY);
    if (saved) {
      var parsed = JSON.parse(saved);
      if (parsed && parsed.effect) {
        jobs += 1;
        var restore = enterQuarantine(parsed.effect, parsed.startedAt, parsed.intent, true, { retry: parsed.retry, retryState: parsed.retryState, recover: parsed.recover }).finally(function() {
          jobs -= 1;
          emitState();
          requestSettle({ force: true });
        });
        queue = queue.then(function() { return restore; }).catch(function() {});
      }
    }
  } catch (e) {}

  if (typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', function() { if (quarantine) recoveryRead(); });
  }
  if (typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', function() { if (quarantine && document.visibilityState !== 'hidden') recoveryRead(); });
  }
})();
